import { Photo, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { mapProductFragmentToProductCardData } from '../../products/common/product-card.mappers';
import SearchUtils from '../../common/utils/search.utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { useWishlistCompaniesByIdsQuery } from '../../queries/__generated__/wishlistCompaniesByIds.generated';
import { useProductsByIdsQuery } from '../../queries/__generated__/productsByIds.generated';
import { currentContextAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import {
  canAccessWishlistAtom,
  wishlistItemsAtom,
  wishlistLoadingAtom,
  wishlistSupplierAtom,
} from '../state';
import {
  ACTIVE_REMINDER_INTERVAL_DAYS,
  PREVIEW_ITEM_LIMIT,
  REMINDER_DISPLAY_DELAY_MS,
  WISHLIST_ACTIVE_REMINDER_SHOWN,
  WISHLIST_LAST_ACTIVITY,
  WISHLIST_LAST_VISIT,
  daysSince,
  getStoredDate,
  isWishlistInactive,
  recordActiveReminderShown,
} from '../wishlist.utils';

export interface WishlistReminderProductPreview {
  id: string;
  name: string;
  photo: Pick<Photo, 'domain' | 'path'> | null;
  family: Product_Sub_Family_Name_Enum;
  priceLabel: string;
}

export interface WishlistReminderSupplierPreview {
  id: string;
  tradeName: string;
  photo: Pick<Photo, 'domain' | 'path'> | null;
}

export interface UseWishlistActiveReminderResult {
  open: boolean;
  onClose: () => void;
  onCtaClick: () => void;
  previewProducts: WishlistReminderProductPreview[];
  previewSuppliers: WishlistReminderSupplierPreview[];
}

export default function useWishlistActiveReminder(): UseWishlistActiveReminderResult {
  const navigate = useNavigate();
  const currencyFormat = useCurrencyFormat();
  const { roleAssignmentId } = useAtomValue(currentContextAtom);
  const canAccessWishlist = useAtomValue(canAccessWishlistAtom);
  const wishlistItems = useAtomValue(wishlistItemsAtom);
  const wishlistSuppliers = useAtomValue(wishlistSupplierAtom);
  const wishlistLoading = useAtomValue(wishlistLoadingAtom);
  const [open, setOpen] = useState(false);

  const previewProductKeys = useMemo(
    () => wishlistItems.slice(0, PREVIEW_ITEM_LIMIT),
    [wishlistItems],
  );

  const previewSupplierIds = useMemo(
    () => wishlistSuppliers.slice(0, PREVIEW_ITEM_LIMIT),
    [wishlistSuppliers],
  );

  const { data: productsData } = useProductsByIdsQuery({
    skip: wishlistLoading || previewProductKeys.length === 0,
    variables: {
      where: {
        _or: previewProductKeys.map(({ supplier_id, sku }) => ({
          company_id: { _eq: supplier_id },
          sku: { _eq: sku },
        })),
      },
    },
  });

  const { data: suppliersData } = useWishlistCompaniesByIdsQuery({
    skip: wishlistLoading || previewSupplierIds.length === 0,
    variables: {
      where: {
        id: { _in: previewSupplierIds.map(({ supplier_id }) => supplier_id) },
      },
    },
  });

  const previewProducts = useMemo((): WishlistReminderProductPreview[] => {
    const productsByKey = new Map(
      (productsData?.product || []).map((product) => [
        `${product.company_id}:${product.sku}`,
        product,
      ]),
    );

    return previewProductKeys
      .map(({ supplier_id, sku }) => productsByKey.get(`${supplier_id}:${sku}`))
      .filter((product) => !!product)
      .map((product) => {
        const card = mapProductFragmentToProductCardData(product);

        return {
          id: card.id,
          name: card.name,
          photo: card.photo,
          family: card.family,
          priceLabel: SearchUtils.getFormatedUnitPrice(card, currencyFormat.format),
        };
      });
  }, [productsData, previewProductKeys]);

  const previewSuppliers = useMemo((): WishlistReminderSupplierPreview[] => {
    const suppliersById = new Map(
      (suppliersData?.company || []).map((company) => [company.id, company]),
    );

    return previewSupplierIds
      .map(({ supplier_id }) => suppliersById.get(supplier_id))
      .filter((supplier) => !!supplier)
      .map((supplier) => ({
        id: supplier.id,
        tradeName: supplier.tradeName,
        photo: supplier.photos[0]?.photo ?? null,
      }));
  }, [suppliersData, previewSupplierIds]);

  const eligible = useMemo(() => {
    if (!canAccessWishlist || wishlistLoading || wishlistItems.length + wishlistSuppliers.length === 0) {
      return false;
    }

    const lastVisit = getStoredDate(WISHLIST_LAST_VISIT, roleAssignmentId);
    const lastActivity = getStoredDate(WISHLIST_LAST_ACTIVITY, roleAssignmentId);
    const lastActiveReminderShown = getStoredDate(WISHLIST_ACTIVE_REMINDER_SHOWN, roleAssignmentId);

    if (!isWishlistInactive(lastVisit, lastActivity, ACTIVE_REMINDER_INTERVAL_DAYS)) {
      return false;
    }

    return !lastActiveReminderShown || daysSince(lastActiveReminderShown) >= ACTIVE_REMINDER_INTERVAL_DAYS;
  }, [
    roleAssignmentId,
    canAccessWishlist,
    wishlistLoading,
    wishlistItems,
    wishlistSuppliers,
  ]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (eligible) {
      timer = setTimeout(() => {
        setOpen(true);
      }, REMINDER_DISPLAY_DELAY_MS);
    } else {
      setOpen(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [eligible]);

  const dismiss = (): void => {
    recordActiveReminderShown(roleAssignmentId);
    setOpen(false);
  };

  const onClose = (): void => {
    dismiss();
  };

  const onCtaClick = (): void => {
    dismiss();
    navigate(generatePath(RoutePaths.WISHLIST));
  };

  return {
    open,
    onClose,
    onCtaClick,
    previewProducts,
    previewSuppliers,
  };
}